// frontend/src/components/header/UserNav.tsx
import capitalize from 'lodash/capitalize';
import { LogOut, Settings, User, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@chainlit/react-client';
import { isAdminConsole } from '@/AppMode';
import { useAppContext } from '@/hooks/useAppContext';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Translator } from 'components/i18n';

export default function UserNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isMaster, isTenant } = useAppContext();

  if (!user) return null;

  const displayName = user?.display_name || user?.identifier;
  const metadata = user?.metadata || {};
  const isAdmin = metadata.role === 'admin';

  const handleAdminClick = () => {
    if (isMaster) {
      navigate('/admin');
    } else if (isTenant) {
      navigate('/users');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id="user-nav-button"
          variant="ghost"
          className="relative h-8 w-8 rounded-full"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={metadata.image || metadata.avatar} alt="user image" />
            <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
              {capitalize(displayName[0])}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.identifier}
            </p>
            <div className="flex items-center gap-1 mt-2">
              {isAdmin && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                  👑 Admin
                </span>
              )}
              {isMaster && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  Master
                </span>
              )}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Lien Administration (seulement si admin) */}
        {isAdmin && (
          <>
            <DropdownMenuItem onClick={handleAdminClick}>
              <Shield className="mr-2 h-4 w-4" />
              <span>
                {isMaster ? 'Administration' : 'Gestion utilisateurs'}
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Profil */}
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          <User className="mr-2 h-4 w-4" />
          <Translator path="navigation.user.menu.profile" />
        </DropdownMenuItem>

        {/* Paramètres (abonnés) : absents de la console master */}
        {!isAdminConsole() && (
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <Settings className="mr-2 h-4 w-4" />
            <Translator path="navigation.user.menu.settings" />
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Déconnexion */}
        <DropdownMenuItem onClick={() => logout(true)}>
          <LogOut className="mr-2 h-4 w-4" />
          <Translator path="navigation.user.menu.logout" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}